import React, { useCallback } from 'react';
import { Typography } from '@mui/material';
import { t } from '../../i18n/t';
import ChartCanvas from './ChartCanvas';
import { CHART_DIAGONAL, CHART_SCATTER, defaultPlot, drawAxes, linMap } from './senseCapChartDraw';

type RrScatterogramChartProps = {
    x: number[];
    y: number[];
};

const RrScatterogramChart = ({ x, y }: RrScatterogramChartProps) => {
    const draw = useCallback(
        (ctx: CanvasRenderingContext2D, size: { width: number; height: number }) => {
            if (x.length < 1 || y.length < 1) return;
            const plot = defaultPlot(size.width, size.height);
            const all = [...x, ...y];
            const rawMin = Math.min(...all);
            const rawMax = Math.max(...all);
            const pad = Math.max((rawMax - rawMin) * 0.08, 1);
            const min = rawMin - pad;
            const max = rawMax + pad;

            drawAxes(
                ctx,
                plot,
                t('sensecap.axisRrMs'),
                t('sensecap.axisNextRrMs'),
                min,
                max,
                min,
                max,
            );

            const x0 = linMap(min, min, max, plot.left, plot.right);
            const y0 = linMap(min, min, max, plot.bottom, plot.top);
            const x1 = linMap(max, min, max, plot.left, plot.right);
            const y1 = linMap(max, min, max, plot.bottom, plot.top);
            ctx.strokeStyle = CHART_DIAGONAL;
            ctx.setLineDash([4, 4]);
            ctx.beginPath();
            ctx.moveTo(x0, y0);
            ctx.lineTo(x1, y1);
            ctx.stroke();
            ctx.setLineDash([]);

            ctx.fillStyle = CHART_SCATTER;
            for (let i = 0; i < Math.min(x.length, y.length); i++) {
                const px = linMap(x[i], min, max, plot.left, plot.right);
                const py = linMap(y[i], min, max, plot.bottom, plot.top);
                ctx.beginPath();
                ctx.arc(px, py, 3.5, 0, Math.PI * 2);
                ctx.fill();
            }
        },
        [x, y],
    );

    if (x.length < 1 || y.length < 1) {
        return (
            <Typography color="text.secondary" variant="body2">
                {t('sensecap.hrvEmpty')}
            </Typography>
        );
    }

    return <ChartCanvas draw={draw} height={240} />;
};

export default RrScatterogramChart;
